import {
  featureAnnouncementPageService,
  type FeatureAnnouncementPageServiceResponse,
} from '@/cmsBackend';
import { logWarn } from '@/cmsBackend/logging';
import type { FeatureAnnouncementPageDefinition } from '@/cmsBackend/transformers/types/featureAnnouncement';
import { resolveCmsEnvOverride } from '@/common/helpers/cmsEnvOverride';
import {
  getEnvDataFromContext,
  getHostnameFromStageAndTLD,
  getUserDataFromContext,
  isPagePreviewRequest,
} from '@/common/helpers/requestHelpers';
import { serve404Page, setCommonPageResponseHeaders } from '@/common/helpers/responseHelpers';
import { getCMSLocaleFromServerLocale } from '@/common/helpers/uiHelpers';
import { Env } from '@/common/types';
import { HeadSEO } from '@/components/seo/HeadSEO';
import { TAG_MANAGER_ENVIRONMENT } from '@/constants/tagManager';
import { FeatureAnnouncementContainer } from '@/containers/FeatureAnnouncementContainer';
import { TagManager } from '@eventbrite/webapp-data';
import { GetServerSideProps, NextPage } from 'next';

interface ServerData {
  env: Env;
  user: {
    userId: string;
  };
  cmsResponse: FeatureAnnouncementPageServiceResponse;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;

  const env = getEnvDataFromContext(context);
  const user = getUserDataFromContext(context);
  const { stage, tld, locale, pagePath } = env;
  // Fixed slug for the listing page; override via ?slug= query param for testing
  const slug = (typeof query.slug === 'string' && query.slug) || 'product-updates';
  const requestedPage = getHostnameFromStageAndTLD(stage, tld) + pagePath;
  const isPreview = Boolean(query.is_preview);

  const cmsEnvOverride = resolveCmsEnvOverride(query.cms_env, isPreview);
  if (cmsEnvOverride.kind === 'invalid') {
    // Next's render404 forces the served status to 404 for a notFound result, so
    // setting 400 here would be overwritten; the JSON API routes return 400.
    logWarn(`[ProductUpdatesPage] Rejected invalid cms_env: ${JSON.stringify(query.cms_env)}`);
    setCommonPageResponseHeaders(context.res, user, false, true);
    return serve404Page();
  }

  console.log(`[ProductUpdatesPage]: ${requestedPage} | `, env);

  const response = await featureAnnouncementPageService({
    requestedPage,
    slug,
    locale: getCMSLocaleFromServerLocale(locale),
    is_preview: isPreview,
    ...(cmsEnvOverride.kind === 'override' && { resolvedEnv: cmsEnvOverride.env }),
  });

  const is5xx = response.isError;
  const is404 = !Object.keys(response.data).length;

  const isError = is5xx || is404;
  const isPreviewRequest = isPagePreviewRequest(context.req, isPreview);
  // An environment override must not be edge-cached: reuse the preview/no-store
  // branch of setCommonPageResponseHeaders (isPagePreviewRequest stays untouched).
  const mustBypassCache = isPreviewRequest || cmsEnvOverride.kind === 'override';
  setCommonPageResponseHeaders(context.res, user, mustBypassCache, isError);

  if (is5xx) {
    context.res.statusCode = 500;
    throw new Error('[ProductUpdatesPage] Error in props.cmsResponse');
  }

  if (is404) {
    return serve404Page();
  }

  const pageProps: ServerData = {
    env,
    user: {
      userId: user.userId,
    },
    cmsResponse: response,
  };

  // JSON round-trip to convert undefined values to null for Next.js serialization
  return {
    props: JSON.parse(JSON.stringify(pageProps)),
  };
};

const ProductUpdatesPage: NextPage<ServerData> = (props) => {
  // Safe cast: empty data case is handled server-side (returns 404)
  const pageData = props.cmsResponse.data as FeatureAnnouncementPageDefinition;

  return (
    <>
      <HeadSEO {...pageData.seo} />
      <FeatureAnnouncementContainer env={props.env} pageData={pageData} />
      <TagManager
        publicUserId={props.user.userId}
        stage={props.env.stage}
        environment={TAG_MANAGER_ENVIRONMENT}
      />
    </>
  );
};

export default ProductUpdatesPage;
